import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsDepartment } from './useData';
import {
  pmsDepartmentTypeField,
  pmsLookupSelect,
  pmsRecordStatusField,
  pmsVesselField} from '../shared/pmsFormFields';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';

export const moduleConfig = {
  module: {
    title: "PMS departments",
    permissionModuleKey: 'pms_hierarchy',
    route: {
      list: '/pms/pms-departments',
      new: '/pms/pms-departments/new',
      detail: (id: number) => `/pms/pms-departments/${id}`,
      edit: (id: number) => `/pms/pms-departments/${id}/edit`},
    api: { list: '/pms-departments', detail: (id: number) => `/pms-departments/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsDepartment[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-departments/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsDepartment) => item.id,
  },
  emptyStates: { noData: 'No records.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsDepartment } = await import('../../../services/mutations');
      await deletePmsDepartment(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: "PMS departments",
    getTitle: (item: PmsDepartment) => (item.name as string) ?? `#${item.id}`},
  form: {
    new: { title: 'New department', submitText: 'Create' },
    edit: { title: 'Edit department', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-4xl',
    create: async (data: Partial<PmsDepartment>) => {
      const { createPmsDepartment } = await import('../../../services/mutations');
      const { vessel, type, recordStatus, parentDepartment, id, ...payload } = data as Record<string, unknown>;
      return createPmsDepartment(payload);
    },
    update: async (id: number, data: Partial<PmsDepartment>) => {
      const { updatePmsDepartment } = await import('../../../services/mutations');
      const { vessel, type, recordStatus, parentDepartment, id: _id, ...payload } = data as Record<string, unknown>;
      return updatePmsDepartment(id, payload);
    }},
  fields: [
    {
      ...pmsVesselField(true),
      helperText: 'Departments are scoped to one vessel’s PMS hierarchy',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('parentDepartmentId', '');
      }},
    {
      ...pmsDepartmentTypeField(true),
      helperText: 'e.g. Engine, Deck, Electrical'},
    {
      ...pmsRecordStatusField(true),
      helperText: 'Lifecycle state of this department record'},
    {
      ...pmsLookupSelect({
        name: 'parentDepartmentId',
        label: 'Parent department',
        apiEndpoint: '/pms-departments?page=1&limit=500',
        required: false,
        clearable: true,
        placeholder: 'None — top-level department',
        optionsDependOnFields: ['vesselId'],
        optionsEndpointFromFormData: (fd) =>
          fd.vesselId
            ? `/pms-departments?page=1&limit=500`
            : '/pms-departments?page=1&limit=500',
        filterDynamicOptionItem: (item, fd) => {
          if (fd.parentDepartmentId && Number(item.id) === Number(fd.parentDepartmentId)) return true;
          if (fd.id && Number(item.id) === Number(fd.id)) return false;
          if (fd.vesselId && Number(item.vesselId) !== Number(fd.vesselId)) return false;
          return true;
        }}),
      helperText: 'Optional. Nest under another department on the same vessel'},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Engine department',
      validation: { required: true }},
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 3,
      placeholder: 'Optional notes about this department’s scope on the vessel'},
    {
      name: 'displayOrder',
      label: 'Display order',
      type: 'number',
      min: 0,
      placeholder: '0',
      helperText: 'Lower numbers appear first in the hierarchy tree'},
    {
      name: 'isActive',
      label: 'Status',
      type: 'switch',
      switchLabel: 'Department is active'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'placement',
        title: 'Placement',
        description: 'Which vessel this department belongs to, and optional nesting.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselId', 'parentDepartmentId'],
        fieldSpans: {
          vesselId: 'md:col-span-6',
          parentDepartmentId: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'identity',
        title: 'Department',
        description: 'Name and type as they appear in the PMS hierarchy.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['name', 'typeId', 'description'],
        fieldSpans: {
          name: 'md:col-span-8',
          typeId: 'md:col-span-4',
          description: 'md:col-span-12'},
        collapsible: false},
      {
        id: 'status',
        title: 'Status & ordering',
        description: 'Record lifecycle and sort position in lists and the tree.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['recordStatusId', 'displayOrder', 'isActive'],
        fieldSpans: {
          recordStatusId: 'md:col-span-5',
          displayOrder: 'md:col-span-3',
          isActive: 'md:col-span-4'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const relName = (
  rel: { code?: string | null; name?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

const labelVessel = (item: PmsDepartment) => {
  const v = item.vessel;
  if (!v && item.vesselId == null) return '-';
  const name = v?.vesselName ?? v?.name;
  return name ?? (item.vesselId != null ? `#${item.vesselId}` : '-');
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsDepartment>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'vesselId',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={labelVessel(row)} />},
  {
    key: 'typeId',
    header: 'Type',
    sortable: true,
    render: (_v, row) => <TextCell value={relName(row.type, row.typeId)} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => <TextCell value={v === false ? 'No' : 'Yes'} />},
];

export const getDetailSections = (item: PmsDepartment, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Vessel', value: labelVessel(item) },
      { label: 'Department type', value: relName(item.type, item.typeId) },
      { label: 'Record status', value: relName(item.recordStatus, item.recordStatusId) },
      { label: 'Parent department', value: relName(item.parentDepartment, item.parentDepartmentId) },
      { label: 'Name', value: item.name != null ? String(item.name) : '' },
      { label: 'Description', value: item.description != null ? String(item.description) : '-' },
      { label: 'Display order', value: item.displayOrder != null ? String(item.displayOrder) : '-' },
      { label: 'Active', value: item.isActive !== false ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsDepartment> => ({
  vesselId: undefined,
  typeId: undefined,
  recordStatusId: undefined,
  parentDepartmentId: undefined,
  name: '',
  description: '',
  displayOrder: undefined,
  isActive: true});
export const mapItemToFormData = (item: PmsDepartment): Partial<PmsDepartment> => ({
  id: item.id,
  vesselId: item.vesselId != null ? Number(item.vesselId) : undefined,
  typeId: item.typeId != null ? Number(item.typeId) : undefined,
  recordStatusId: item.recordStatusId != null ? Number(item.recordStatusId) : undefined,
  parentDepartmentId: item.parentDepartmentId != null ? Number(item.parentDepartmentId) : undefined,
  // Keep relations so edit selects can pin the current label before/while options load.
  vessel: item.vessel ?? undefined,
  type: item.type ?? undefined,
  recordStatus: item.recordStatus ?? undefined,
  parentDepartment: item.parentDepartment ?? undefined,
  name: item.name ?? '',
  description: item.description ?? '',
  displayOrder: item.displayOrder != null ? Number(item.displayOrder) : undefined,
  isActive: item.isActive ?? true});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
